import type { ModulatorRepository } from "~application/devices/ports/modulator-repository.ts";
import type { AcqRate, AcqRateKind } from "~domain/devices/models/acq-rate-model.ts";
import type { ModulatorLink } from "~domain/devices/models/links/link-model.ts";
import type { ModulatorConsumption } from "~domain/devices/models/modulators/modulator-consumption.ts";
import type { ModulatorLastPoint } from "~domain/devices/models/modulators/modulator-last-point-model.ts";
import type { ModulatorLastTemperaturePoint } from "~domain/devices/models/modulators/modulator-last-temperature-point-model.ts";
import type { Modulator, ModulatorCsId, ModulatorHwId } from "~domain/devices/models/modulators/modulator-model.ts";
import type { LocaleDate } from "~domain/global/models/date-model.ts";
import type { BackofficeFindLinksResponse } from "~infrastructure/api/backoffice/contract/devices/responses/backoffice-find-links-response.ts";
import type { BackofficeApi } from "~infrastructure/api/backoffice/http/backoffice-tsrest-client.ts";

import {
  DomainError,
  DomainErrorCode,
  DomainNotFoundError,
  DomainTransportError,
  DomainUnauthorizedError,
} from "~domain/global/errors/domain-error.ts";
import { ModulatorMapper } from "~infrastructure/devices/mappers/modulator-mapper.ts";
import { isDefined } from "~utils/collection.ts";

export class BackofficeModulatorRepository implements ModulatorRepository {
  constructor(private readonly backoffice: BackofficeApi) {}

  async findModulator(modulatorHwId: ModulatorHwId): Promise<Modulator> {
    const response = await this.backoffice.devices.findModulatorDetails({
      params: {
        modulatorHwId: modulatorHwId,
      },
    });

    if (response.status === 200) {
      const modulator = ModulatorMapper.fromBackofficeModulatorToDomainModulator(response.body);

      if (!modulator) {
        throw new DomainNotFoundError(`Not found modulator with hw id ${modulatorHwId}`);
      }

      let lastTemperaturePoint: ModulatorLastTemperaturePoint | undefined;
      try {
        const response = await this.findModulatorsLastTemperaturePoint([modulator]);
        lastTemperaturePoint = response.find((s) => s.csId === modulator.csId);
      } catch {}

      const links = await this.findModulatorLinks(modulatorHwId);

      return {
        ...modulator,
        lastTemperaturePointDate: lastTemperaturePoint?.date,
        links,
      };
    }

    if (response.status === 500) {
      throw new DomainError(DomainErrorCode.UNEXPECTED_ERROR, response.body.message);
    }

    if (response.status === 403) {
      throw new DomainUnauthorizedError();
    }

    throw new DomainTransportError(`Unexpected error`);
  }

  async findModulatorAcqRate(modulatorHwId: ModulatorHwId, kind: AcqRateKind): Promise<AcqRate> {
    const response = await (async () => {
      switch (kind) {
        case "DAY":
          return this.findModulatorAcqRateLastDay(modulatorHwId);
        case "WEEK":
          return this.findModulatorAcqRateLastWeek(modulatorHwId);
        case "MONTH":
          return this.findModulatorAcqRateLastMonth(modulatorHwId);
      }
    })();

    if (response.status === 200) {
      return response.body;
    }

    if (response.status === 500) {
      throw new DomainError(DomainErrorCode.UNEXPECTED_ERROR, response.body.message);
    }

    if (response.status === 403) {
      throw new DomainUnauthorizedError();
    }

    throw new DomainTransportError(`Unexpected error`);
  }

  async findModulatorLastPoint(modulatorCsId: ModulatorCsId): Promise<ModulatorLastPoint> {
    const response = await this.backoffice.devices.findModulatorLastPoint({
      params: {
        modulatorCsId: modulatorCsId,
      },
    });

    if (response.status === 200) {
      const lastPoint = ModulatorMapper.fromBackofficeLastPointToDomainLastPoint(modulatorCsId, response.body);
      if (!lastPoint) {
        throw new DomainNotFoundError(`Last point not found for modulator cs id ${modulatorCsId}`);
      }

      return lastPoint;
    }

    if (response.status === 500) {
      throw new DomainError(DomainErrorCode.UNEXPECTED_ERROR, response.body.message);
    }

    if (response.status === 403) {
      throw new DomainUnauthorizedError();
    }

    throw new DomainTransportError(`Unexpected error`);
  }

  async findModulatorConsumptions(
    modulatorCsId: ModulatorCsId,
    start: LocaleDate,
    end: LocaleDate,
  ): Promise<ModulatorConsumption> {
    const response = await this.backoffice.devices.findModulatorConsumptions({
      params: {
        modulatorCsId: modulatorCsId,
      },
      query: {
        start: start,
        end: end,
      },
    });

    if (response.status === 200) {
      const consumption = ModulatorMapper.fromBackofficeModulatorConsumptionsToDomainConsumptions(
        modulatorCsId,
        response.body,
      );
      if (!consumption) {
        throw new DomainNotFoundError(`Consumption not found for modulator cs id ${modulatorCsId}`);
      }

      return consumption;
    }

    if (response.status === 500) {
      throw new DomainError(DomainErrorCode.UNEXPECTED_ERROR, response.body.message);
    }

    if (response.status === 403) {
      throw new DomainUnauthorizedError();
    }

    throw new DomainTransportError(`Unexpected error`);
  }

  async findModulatorLinks(modulatorHwId: ModulatorHwId): Promise<Array<ModulatorLink>> {
    const response = await this.backoffice.devices.findModulatorLinks({
      params: {
        modulatorHwId: modulatorHwId,
      },
    });

    if (response.status === 200) {
      if (!response.body || response.body instanceof Blob) {
        throw new DomainNotFoundError(`Modulator links not found for modulator hw id ${modulatorHwId}`);
      }

      const appliancePromises = await Promise.allSettled(
        response.body.map((rawLink) => this.findLinkAppliances(rawLink)),
      );

      return (
        appliancePromises.filter((r) => {
          if (r.status === "rejected") {
            console.error(r.reason);
            return false;
          }

          return true;
        }) as Array<{ value: ModulatorLink }>
      ).map((r) => r.value);
    }

    if (response.status === 500) {
      throw new DomainError(DomainErrorCode.UNEXPECTED_ERROR, response.body.message);
    }

    if (response.status === 403) {
      throw new DomainUnauthorizedError();
    }

    throw new DomainTransportError(`Unexpected error`);
  }

  private async findLinkAppliances(rawLink: BackofficeFindLinksResponse[number]): Promise<ModulatorLink> {
    const response = await this.backoffice.devices.findLinkAppliances({
      params: {
        linkCsId: rawLink.csId,
      },
    });

    if (response.status === 200) {
      const link = ModulatorMapper.fromBackofficeLinkApplianceToModel(rawLink, response.body);

      return link;
    }

    if (response.status === 500) {
      throw new DomainError(DomainErrorCode.UNEXPECTED_ERROR, response.body.message);
    }

    if (response.status === 403) {
      throw new DomainUnauthorizedError();
    }

    throw new DomainTransportError(`Unexpected error`);
  }

  private async findModulatorAcqRateLastDay(modulatorHwId: ModulatorHwId) {
    return this.backoffice.devices.findModulatorAcqRateLastDay({
      params: {
        modulatorHwId: modulatorHwId,
      },
    });
  }
  private async findModulatorAcqRateLastWeek(modulatorHwId: ModulatorHwId) {
    return this.backoffice.devices.findModulatorAcqRateLastWeek({
      params: {
        modulatorHwId: modulatorHwId,
      },
    });
  }
  private async findModulatorAcqRateLastMonth(modulatorHwId: ModulatorHwId) {
    return this.backoffice.devices.findModulatorAcqRateLastMonth({
      params: {
        modulatorHwId: modulatorHwId,
      },
    });
  }

  async findModulatorsLastTemperaturePoint(
    modulators: Array<Pick<Modulator, "csId">>,
  ): Promise<Array<ModulatorLastTemperaturePoint>> {
    const response = await this.backoffice.devices.findModulatorsLastTemperaturePoint({
      query: {
        csModulators: modulators.map((mod) => mod.csId).filter(isDefined),
      },
    });

    if (response.status === 200) {
      return ModulatorMapper.fromBackofficeLastTemperaturePointsToDomain(response.body);
    }

    if (response.status === 500) {
      throw new DomainError(DomainErrorCode.UNEXPECTED_ERROR, response.body.message);
    }

    if (response.status === 403) {
      throw new DomainUnauthorizedError();
    }

    throw new DomainTransportError(`Unexpected error`);
  }
}
